import {
  Button,
  Card,
  CardBody,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { useWaterfallAuditExcelWorkbookCreate } from "gen/endpoints/fund-family-event/fund-family-event";
import { WaterfallDistributionData } from "gen/model/waterfallDistributionData";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  CardSection,
  CardSectionBody,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import useDocumentPoll from "shared/hooks/useDocumentPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatIsoDateToSummary } from "shared/utils/DateUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const DistributionStats: React.FC<{
  data?: WaterfallDistributionData;
  isEqualizedView: boolean;
  setIsEqualizedView: (isEqualizedView: boolean) => void;
  onEditAdjustmentsClick?: () => void;
  showAdjustmentsButton?: boolean;
  fundId: string;
}> = ({
  data,
  isEqualizedView,
  setIsEqualizedView,
  onEditAdjustmentsClick,
  showAdjustmentsButton = true,
  fundId,
}) => {
  const { t } = useTranslation(["distribution", "waterfall"]);

  const { fundFamilyEventId, fundFamilyId, fundFamilySubEventId } =
    useRequiredStringParams();

  const { mutateAsync: downloadAudit } = useWaterfallAuditExcelWorkbookCreate();

  const { setDocumentId } = useDocumentPoll(
    t("waterfall:waterfallAudit.success"),
    t("waterfall:waterfallAudit.error"),
    t("waterfall:waterfallAudit.start"),
  );

  if (!data) {
    return null;
  }
  return (
    <Card variant="stat" size="xs">
      <CardBody>
        <CardSectionGroup>
          <CardSection>
            <CardSectionBody>
              <StatGroup>
                <Stat size="xs">
                  <StatLabel>
                    {t("distribution:waterfallFund.totalDistribution")}
                  </StatLabel>
                  <StatNumber>
                    {formatMoney(data.total_distribution_amount)}
                  </StatNumber>
                </Stat>
                <Stat size="xs">
                  <StatLabel>
                    {t("distribution:waterfallFund.distributionDate")}
                  </StatLabel>
                  <StatNumber>
                    {formatIsoDateToSummary(data.distribution_date)}
                  </StatNumber>
                </Stat>
              </StatGroup>
            </CardSectionBody>
          </CardSection>
          <CardSection>
            <CardSectionBody>
              <StatGroup>
                <EqualizedViewToggleButton
                  isEqualizedView={isEqualizedView}
                  setIsEqualizedView={setIsEqualizedView}
                  size="lg"
                  actualTooltip={t("waterfall:toolTips.actualToolTip")}
                  equalizedTooltip={t("waterfall:toolTips.equalizedToolTip")}
                />
              </StatGroup>
            </CardSectionBody>
          </CardSection>
          {showAdjustmentsButton && (
            <CardSection>
              <CardSectionBody>
                <StatGroup>
                  <Button
                    size="xs"
                    colorScheme="tertiary"
                    onClick={onEditAdjustmentsClick}
                  >
                    {t("distribution:adjustments.adjustWaterfall")}
                  </Button>
                </StatGroup>
              </CardSectionBody>
            </CardSection>
          )}
          <CardSection>
            <CardSectionBody>
              <StatGroup>
                <Button
                  size="xs"
                  colorScheme="secondary"
                  onClick={async () => {
                    const workbookDocument = await downloadAudit({
                      fundFamilyId,
                      fundFamilyEventId,
                      fundFamilySubEventId,
                      fundEntityId: fundId,
                      params: {
                        is_equalized_view: isEqualizedView || undefined,
                      },
                    });
                    setDocumentId(workbookDocument.id);
                  }}
                >
                  {t("waterfall:waterfallAudit.download")}
                </Button>
              </StatGroup>
            </CardSectionBody>
          </CardSection>
        </CardSectionGroup>
      </CardBody>
    </Card>
  );
};
